import React from 'react'
import { t } from 'i18next'
import { isNil } from 'lodash'
import { SubActivity, ActivityAction, ActivityObjectType, ActivityObject } from '../../../generated/types-orchestration'
import { Button, Icon, Tooltip } from '@awell-health/design-system'
import { ExpandableContent } from '../ExpandableContent'

const getRemindersCountTranslation = (subActivity: SubActivity, subActivities: SubActivity[]): string => {
  const count = subActivities.filter(
    ({ action, date }) => date <= subActivity.date && action === ActivityAction.Remind
  ).length
  return t(`activityfeed:count`, { count })
}

const createMessageSubActivitySummaryText = (
  subActivities: SubActivity[],
  subActivity: SubActivity,
  object: ActivityObject,
  indirect_object: ActivityObject
): string => {
  switch (subActivity.action) {
    case ActivityAction.Activate:
      return t('activityfeed:message_active', {
        indirect_object,
        object
      })
    case ActivityAction.Deliver:
      return t('activityfeed:message_delivered', {
        object,
        indirect_object
      })
    case ActivityAction.Read:
      return t('activityfeed:message_read', subActivity)
    case ActivityAction.Send:
      return t('activityfeed:message_sent', {
        object,
        indirect_object
      })
    case ActivityAction.Skipped:
      return (
        subActivity.text?.en ??
        t('activityfeed:message_skipped', {
          object,
          indirect_object
        })
      )
    case ActivityAction.Expired:
      return t('activityfeed:message_expired', { object })
    case ActivityAction.Generated:
      return t('activityfeed:clinical_note_generated')
    case ActivityAction.Remind: {
      const email = isNil(indirect_object) || isNil(indirect_object?.email) ? '' : `(${indirect_object?.email})`
      return t('activityfeed:reminder_event', {
        firstName: indirect_object?.name,
        email,
        reminderCount: getRemindersCountTranslation(subActivity, subActivities)
      })
    }
    default:
      return ''
  }
}

const createAgentSubActivitySummaryText = (subActivity: SubActivity): string | JSX.Element => {
  switch (subActivity.action) {
    case ActivityAction.Delegated:
      return t('activityfeed:agent_action_delegated', {
        name: subActivity.object?.name
      })
    case ActivityAction.Skipped:
      return t('activityfeed:agent_action_skipped', {
        name: subActivity.object?.name
      })
    case ActivityAction.Complete:
      return t('activityfeed:agent_action_completed', {
        name: subActivity.object?.name
      })
    case ActivityAction.Reported: {
      const errorMessage = subActivity.text?.en ?? ''
      const errorCategory = subActivity.error_category ?? ''
      const errorDetails = subActivity.error ?? ''
      const message = [errorMessage, errorCategory, errorDetails].join('\n\n')
      t('activityfeed:agent_action_reported', {
        name: subActivity.object?.name
      })
      return (
        <div className="flex flex-col gap-1">
          <p className=" text-base font-normal text-gray-900">
            {t('activityfeed:extension_action_reported', {
              subject: subActivity.subject
            })}
          </p>
          <ExpandableContent content={message} />
        </div>
      )
    }
    case ActivityAction.Failed:
      return t('activityfeed:agent_action_failed', {
        name: subActivity.object?.name
      })
    default:
      return ''
  }
}

const createDecisionSubActivitySummaryText = (subActivity: SubActivity): string => {
  return subActivity.text?.en ?? ''
}

const createTimerSubActivitySummaryText = (subActivity: SubActivity): string => {
  return subActivity.text?.en ?? ''
}

const createClinicalNoteSubActivitySummaryText = (subActivity: SubActivity): string => {
  switch (subActivity.action) {
    case ActivityAction.Generated:
      return t('activityfeed:clinical_note_generated')
    default:
      return ''
  }
}

const createExtensionSubActivitySummaryText = (subActivity: SubActivity): string | JSX.Element => {
  const { text, error, error_category } = subActivity
  switch (subActivity.action) {
    case ActivityAction.Delegated:
      return t('activityfeed:extension_action_delegated', {
        object: subActivity.object,
        subject: subActivity.subject
      })
    case ActivityAction.Skipped:
      return t('activityfeed:extension_action_skipped')
    case ActivityAction.Complete:
      return t('activityfeed:extension_action_completed', {
        subject: subActivity.subject
      })
    case ActivityAction.Reported: {
      const errorMessage = text?.en ?? ''
      const errorCategory = error_category ?? ''
      const errorDetails = error ?? ''
      const message = [errorMessage, errorCategory, errorDetails].join('\n\n')
      t('activityfeed:extension_action_reported', {
        subject: subActivity.subject
      })
      return (
        <div className="flex flex-col gap-1">
          <p className=" text-sm font-normal text-gray-900">
            {t('activityfeed:extension_action_reported', {
              subject: subActivity.subject
            })}
          </p>
          <ExpandableContent content={message} />
        </div>
      )
    }
    case ActivityAction.Failed:
      return t('activityfeed:extension_action_failed', {
        subject: subActivity.subject
      })
    default:
      return ''
  }
}

const createFormSubActivitySummaryText = (
  subActivities: SubActivity[],
  subActivity: SubActivity,
  object: ActivityObject,
  indirect_object: ActivityObject
): string => {
  const { subject } = subActivity

  switch (subActivity.action) {
    case ActivityAction.Activate:
      return t('activityfeed:form_submit_active', {
        indirect_object,
        object
      })
    case ActivityAction.Submitted:
      return t('activityfeed:form_submitted', { subject, object })
    case ActivityAction.Remind:
      return t('activityfeed:reminder_event', {
        firstName: indirect_object?.name,
        email: indirect_object?.email ?? '',
        reminderCount: getRemindersCountTranslation(subActivity, subActivities)
      })
    case ActivityAction.Skipped:
      return subActivity.text?.en ?? t('activityfeed:form_skipped', { object, indirect_object })
    case ActivityAction.Expired:
      return t('activityfeed:form_expired', { object })
    default:
      return ''
  }
}

const createChecklistSubActivitySummaryText = (
  subActivities: SubActivity[],
  subActivity: SubActivity,
  object: ActivityObject,
  indirect_object: ActivityObject
): string => {
  const { subject } = subActivity

  switch (subActivity.action) {
    case ActivityAction.Activate:
      return t('activityfeed:checklist_submit_active', {
        indirect_object,
        object
      })
    case ActivityAction.Submitted:
      return t('activityfeed:checklist_submitted', {
        subject,
        object
      })
    case ActivityAction.Remind:
      return t('activityfeed:reminder_event', {
        firstName: indirect_object?.name,
        email: indirect_object?.email ?? '',
        reminderCount: getRemindersCountTranslation(subActivity, subActivities)
      })
    default:
      return ''
  }
}

const createCalculationSubActivitySummaryText = (subActivity: SubActivity, object: ActivityObject): string => {
  switch (subActivity.action) {
    case ActivityAction.Activate:
      return t('activityfeed:calculation_activated', {
        object
      })
    case ActivityAction.Computed:
      return t('activityfeed:calculation_computed', {
        object
      })
    case ActivityAction.Failed:
      return t('activityfeed:calculation_failed', { object })
    default:
      return ''
  }
}

const createEmrReportSubActivitySummaryText = (subActivity: SubActivity): string | JSX.Element => {
  const emrSentTranslation = t('activityfeed:emr_sent', {
    returnObjects: true
  })
  const emrSendFailureTranslation = t('activityfeed:emr_send_failure', {
    returnObjects: true
  })
  const emrSuccessTranslation = t('activityfeed:emr_processed_success', {
    returnObjects: true
  })
  const emrFailureTranslation = t('activityfeed:emr_processed_failure', {
    returnObjects: true
  })

  const request_id = subActivity.object?.id
  const requestIdBtn = !isNil(request_id) ? (
    <Tooltip datatip={`Copy request id(${request_id}) to clipboard`}>
      <Button
        variant="ghost"
        onClick={() => {
          void navigator.clipboard.writeText(request_id)
        }}
        style={{ padding: 0 }}
        iconSize="xs"
      >
        <Icon icon="RiFileCopyFill" size={16} />
      </Button>
    </Tooltip>
  ) : (
    <></>
  )
  switch (subActivity.action) {
    case ActivityAction.Send:
      return (
        <span>
          {emrSentTranslation[0]}
          {requestIdBtn}
          {emrSentTranslation[1]}
        </span>
      )
    case ActivityAction.FailedToSend:
      return (
        <span>
          {emrSendFailureTranslation[0]}
          {requestIdBtn}
          {emrSendFailureTranslation[1]}
        </span>
      )
    case ActivityAction.Processed:
      return (
        <span>
          {emrSuccessTranslation[0]}
          {requestIdBtn}
          {emrSuccessTranslation[1]}
        </span>
      )
    case ActivityAction.Failed:
      return (
        <span>
          {emrFailureTranslation[0]}
          {requestIdBtn}
          {emrFailureTranslation[1]}
        </span>
      )
    default:
      return ''
  }
}

const createApiCallSubActivitySummaryText = (subActivity: SubActivity): string => {
  switch (subActivity.action) {
    case ActivityAction.Complete:
      return t('activityfeed:api_call_completed')
    case ActivityAction.Send:
      return t('activityfeed:api_call_sent')
    case ActivityAction.Failed:
      return t('activityfeed:api_call_failed')
    default:
      return ''
  }
}

const createSubActivitySummaryText = (
  subActivities: SubActivity[],
  subActivity: SubActivity,
  object: ActivityObject,
  indirect_object?: ActivityObject
): string | JSX.Element => {
  switch (object.type) {
    case ActivityObjectType.ClinicalNote:
      return createClinicalNoteSubActivitySummaryText(subActivity)
    case ActivityObjectType.Message:
      return createMessageSubActivitySummaryText(subActivities, subActivity, object, indirect_object!)
    case ActivityObjectType.Form:
      return createFormSubActivitySummaryText(subActivities, subActivity, object, indirect_object!)
    case ActivityObjectType.Checklist:
      return createChecklistSubActivitySummaryText(subActivities, subActivity, object, indirect_object!)
    case ActivityObjectType.Calculation:
      return createCalculationSubActivitySummaryText(subActivity, object)
    case ActivityObjectType.EmrReport:
      return createEmrReportSubActivitySummaryText(subActivity)
    case ActivityObjectType.ApiCall:
      return createApiCallSubActivitySummaryText(subActivity)
    case ActivityObjectType.PluginAction:
      return createExtensionSubActivitySummaryText(subActivity)
    case ActivityObjectType.Decision:
      return createDecisionSubActivitySummaryText(subActivity)
    case ActivityObjectType.Timer:
      return createTimerSubActivitySummaryText(subActivity)
    case ActivityObjectType.Agent:
      return createAgentSubActivitySummaryText(subActivity)
    default:
      return ''
  }
}

export { createSubActivitySummaryText }
